import type { UsageProvider } from "@listou/shared/schemas/admin";
import type { AdminUsageResponse } from "@listou/shared/types/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Box, Grid, n, Pending, Spark, Table, usd, useAdminDays } from "../components/admin/retro";
import {
  dailyUsage,
  PROVIDER_LABELS,
  ScrapeHostsBox,
  UsageCharts,
  UsageOperationsBox,
  UsageTotalsBox,
} from "../components/admin/usage";
import { adminUsageQuery } from "../lib/queries";

export const Route = createFileRoute("/_authed/admin/conso")({
  component: UsagePage,
});

const DAILY: UsageProvider[] = ["openai", "scrapingbee", "mail", "typesafe"];

function UsagePage() {
  const days = useAdminDays();
  const usage = useQuery(adminUsageQuery(days));
  if (!usage.data) return <Pending query={usage} />;
  return (
    <div className={usage.isPlaceholderData ? "opacity-60" : undefined}>
      <Grid>
        <UsageTotalsBox usage={usage.data} className="md:col-span-2 xl:col-span-4" />
        <ScrapeHostsBox usage={usage.data} className="md:col-span-2 xl:col-span-4" />
        <UsageCharts usage={usage.data} />
        <UsageOperationsBox usage={usage.data} className="md:col-span-2" />
        <DailyBox usage={usage.data} />
      </Grid>
    </div>
  );
}

/** Day by day, most recent first: a spike is easier to date here than on a sparkline. */
function DailyBox({ usage }: { usage: AdminUsageResponse }) {
  const days = [...new Set(usage.series.map((entry) => entry.day))].toSorted().toReversed();
  const at = (day: string, provider: UsageProvider) =>
    usage.series.find((entry) => entry.day === day && entry.provider === provider);
  return (
    <Box title="par jour" className="md:col-span-2">
      <div className="mb-1 grid grid-cols-2 gap-x-4">
        {DAILY.map((provider) => (
          <div key={provider} className="flex justify-between gap-2">
            <span style={{ color: "var(--r-dim)" }}>{PROVIDER_LABELS[provider]}</span>
            <Spark values={dailyUsage(usage, provider, (entry) => entry.costUsd || entry.units)} />
          </div>
        ))}
      </div>
      <Table
        head={["jour", "openai", "openai $", "bee crédits", "mails", "typesafe", "échecs"]}
        rows={days.map((day) => {
          const failures = usage.series
            .filter((entry) => entry.day === day)
            .reduce((total, entry) => total + entry.failures, 0);
          return [
            day,
            at(day, "openai")?.calls ?? 0,
            usd(at(day, "openai")?.costUsd ?? 0),
            at(day, "scrapingbee")?.units ?? 0,
            at(day, "mail")?.calls ?? 0,
            at(day, "typesafe")?.calls ?? 0,
            { value: failures ? n(failures) : "-", tone: failures ? "bad" : "dim" },
          ];
        })}
        empty="aucun appel sur la période"
      />
    </Box>
  );
}
